import type { Variants } from "framer-motion";

/**
 * Shared motion surface consumed by every landing section.
 *
 * Values mirror `tokens.motion` from `src/lib/design-tokens.ts`. Keeping
 * both copies is intentional: the tokens module stays pure TS data, while
 * this module exposes ready-to-use framer-motion objects (`Transition`,
 * `Variants`) so sections never reinvent their own curves or variants.
 */

export const easings = {
  standard: [0.22, 1, 0.36, 1] as const,
  emphasized: [0.2, 0, 0, 1] as const,
  out: [0.16, 1, 0.3, 1] as const,
};

export const durations = {
  fast: 0.2,
  base: 0.35,
  slow: 0.6,
  cinema: 0.9,
} as const;

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: durations.base, ease: easings.standard },
  },
};

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.slow, ease: easings.standard },
  },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.base, ease: easings.out },
  },
};

export const staggerChildren = (stagger = 0.08, delayChildren = 0.05): Variants => ({
  hidden: {},
  show: {
    transition: { staggerChildren: stagger, delayChildren },
  },
});

/**
 * Parallax helper for hero / section foreground layers.
 * Returns Variants keyed by vertical offset in pixels.
 */
export const parallaxY = (offset = 40): Variants => ({
  hidden: { y: offset, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: durations.slow, ease: easings.standard },
  },
});

/**
 * Reduced-motion collapse: every variant above becomes opacity-only.
 * Components should prefer the `<Reveal>` primitive, which handles this
 * automatically via `useReducedMotion`.
 */
export const reducedVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: durations.fast } },
};
/**
 * Shared framer-motion presets — populated as sections are rebuilt
 * in prompts 04–07. Keep runtime-safe (no `"use client"` needed here).
 */
export {};
